import React, { useState, useEffect, useRef } from "react";
import { Live, LiveFilters, Country, Spot } from "@/common/model/Live";
import { SpotRequest } from "@/common/model/SpotRequest";
import { LiveService } from "@/services/LiveService";
import { useAuth } from "@/context/AuthContext";
import {
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import MapSearchBar from "./MapSearchBar";

interface LiveFiltersComponentProps {
  filters: LiveFilters;
  onFilterChange: (filters: LiveFilters) => void;
  countries: Country[];
  sessions: Live[];
  onSpotSelect: (spot: Spot) => void;
  onLocationSelect: (lat: number, lng: number, name: string) => void;
  onUserSelect: (sessionId: number) => void;
  hideAdvancedFilters?: boolean;
  // Request mode props
  spotRequests?: SpotRequest[];
  onRequestSelect?: (request: SpotRequest) => void;
}

const LiveFiltersComponent: React.FC<LiveFiltersComponentProps> = ({
  filters,
  onFilterChange,
  countries,
  sessions,
  onSpotSelect,
  onLocationSelect,
  onUserSelect,
  hideAdvancedFilters = false,
  spotRequests = [],
  onRequestSelect,
}) => {
  const { user } = useAuth();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [spotSearch, setSpotSearch] = useState("");
  const [spots, setSpots] = useState<Spot[]>([]);
  const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showSpotDropdown, setShowSpotDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const spotDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
      if (spotDropdownRef.current && !spotDropdownRef.current.contains(event.target as Node)) {
        setShowSpotDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter countries based on search
  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Search spots when typing - use admin search for complete results
  useEffect(() => {
    const searchSpots = async () => {
      if (spotSearch.length < 2) {
        setSpots([]);
        return;
      }

      const results = await LiveService.adminSearchSpots(spotSearch, 20);
      // Map admin results to Spot format and filter by country if set
      const mappedSpots: Spot[] = results
        .filter(r => !filters.countryId || r.countryId === filters.countryId)
        .map(r => ({
          id: r.id,
          name: r.name,
          latitude: r.latitude,
          longitude: r.longitude,
          country: r.countryName || "",
          alpha2: r.alpha2 || "",
          countryid: r.countryId,
        }));
      setSpots(mappedSpots);
      setShowSpotDropdown(true);
    };

    const debounce = setTimeout(searchSpots, 300);
    return () => clearTimeout(debounce);
  }, [spotSearch, filters.countryId]);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setCountrySearch(country.name);
    setShowCountryDropdown(false);
    setSelectedSpot(null);
    setSpotSearch("");
    onFilterChange({
      ...filters,
      countryId: country.id,
      spotId: undefined,
    });
  };

  const handleClearCountry = () => {
    setSelectedCountry(null);
    setCountrySearch("");
    setSelectedSpot(null);
    setSpotSearch("");
    onFilterChange({
      ...filters,
      countryId: undefined,
      spotId: undefined,
    });
  };

  const handleSpotSelect = (spot: Spot) => {
    setSelectedSpot(spot);
    setSpotSearch(spot.name);
    setShowSpotDropdown(false);
    onFilterChange({ ...filters, spotId: spot.id });
  };

  const handleClearSpot = () => {
    setSelectedSpot(null);
    setSpotSearch("");
    onFilterChange({ ...filters, spotId: undefined });
  };

  const handleSortChange = (sort: string) => {
    onFilterChange({ ...filters, sort });
  };

  const handleSortDirChange = (sortDir: "ASC" | "DESC") => {
    onFilterChange({ ...filters, sortDir });
  };

  const handleGenderChange = (gender: string) => {
    onFilterChange({ ...filters, gender: gender || undefined });
  };

  const handleDeviceOsChange = (deviceOs: string) => {
    onFilterChange({ ...filters, deviceOs: deviceOs as "ios" | "android" | undefined || undefined });
  };

  const handleDeviceTypeChange = (deviceType: string) => {
    onFilterChange({ ...filters, deviceType: deviceType as "phone" | "watch" | undefined || undefined });
  };

  const clearAllFilters = () => {
    setSelectedCountry(null);
    setCountrySearch("");
    setSelectedSpot(null);
    setSpotSearch("");
    onFilterChange({
      sort: "name",
      sortDir: "ASC",
    });
  };

  const hasActiveFilters =
    filters.countryId ||
    filters.spotId ||
    filters.gender ||
    filters.deviceOs ||
    filters.deviceType;

  return (
    <div className="w-full space-y-2">
      {/* Compact Search and Filter Row */}
      <div className="flex gap-2 items-center">
        {/* Map Search (Spots, Locations, Riders, Requests) */}
        <MapSearchBar
          sessions={sessions}
          onSpotSelect={onSpotSelect}
          onLocationSelect={onLocationSelect}
          onUserSelect={onUserSelect}
          spotRequests={spotRequests}
          onRequestSelect={onRequestSelect}
        />

        {/* Toggle Advanced Filters - hidden in request mode */}
        {!hideAdvancedFilters && (
          <>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${
                showAdvanced || hasActiveFilters
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <FunnelIcon className="w-4 h-4" />
              {hasActiveFilters && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${showAdvanced || hasActiveFilters ? "bg-blue-500" : "bg-gray-300"}`}>
                  {[filters.countryId, filters.spotId, filters.gender, filters.deviceOs, filters.deviceType].filter(Boolean).length}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear filters"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Advanced Filters - Expanded Panel */}
      {showAdvanced && !hideAdvancedFilters && (
        <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 p-3 bg-gray-100 rounded-lg border border-gray-200">
          {/* Country Filter - Searchable */}
          <div className="relative" ref={countryDropdownRef}>
            <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setShowCountryDropdown(true);
                  if (e.target.value === "") {
                    handleClearCountry();
                  }
                }}
                onFocus={() => setShowCountryDropdown(true)}
                className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {selectedCountry && (
                <button
                  onClick={handleClearCountry}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  <XMarkIcon className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            {showCountryDropdown && filteredCountries.length > 0 && !selectedCountry && (
              <div className="absolute z-[1000] w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto">
                {filteredCountries.slice(0, 20).map((country) => (
                  <button
                    key={country.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleCountrySelect(country);
                    }}
                    className="w-full px-2 py-1.5 text-left text-gray-900 hover:bg-gray-100 transition-colors flex items-center gap-2 text-sm"
                  >
                    {country.alpha2 && (
                      <img
                        src={`https://flagcdn.com/w20/${country.alpha2.toLowerCase()}.png`}
                        alt={country.alpha2}
                        className="w-4 h-auto"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    )}
                    <span>{country.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Spot Search */}
          <div className="relative" ref={spotDropdownRef}>
            <label className="block text-xs font-medium text-gray-700 mb-1">Spot</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search (min 2 chars)..."
                value={spotSearch}
                onChange={(e) => {
                  setSpotSearch(e.target.value);
                  if (e.target.value.length >= 2) {
                    setShowSpotDropdown(true);
                  }
                }}
                onFocus={() => spots.length > 0 && setShowSpotDropdown(true)}
                className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {selectedSpot && (
                <button
                  onClick={handleClearSpot}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  <XMarkIcon className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            {showSpotDropdown && spots.length > 0 && !selectedSpot && (
              <div className="absolute z-[1000] w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto">
                {spots.map((spot) => (
                  <button
                    key={spot.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSpotSelect(spot);
                    }}
                    className="w-full px-2 py-1.5 text-left text-gray-900 hover:bg-gray-100 transition-colors text-sm"
                  >
                    <div className="font-medium">{spot.name}</div>
                    <div className="text-xs text-gray-500">{spot.alpha2}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Gender Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
            <select
              value={filters.gender || ""}
              onChange={(e) => handleGenderChange(e.target.value)}
              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          {/* Device OS Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">OS</label>
            <select
              value={filters.deviceOs || ""}
              onChange={(e) => handleDeviceOsChange(e.target.value)}
              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="ios">iOS</option>
              <option value="android">Android</option>
            </select>
          </div>

          {/* Device Type Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Device</label>
            <select
              value={filters.deviceType || ""}
              onChange={(e) => handleDeviceTypeChange(e.target.value)}
              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="phone">Phone</option>
              <option value="watch">Watch</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={filters.sort || "name"}
              onChange={(e) => handleSortChange(e.target.value)}
              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="name">Name</option>
              <option value="maxheight">Max Height</option>
              <option value="spot">Spot</option>
              <option value="country">Country</option>
              <option value="sessiontime">Duration</option>
              <option value="lastgpsupdate">Last Update</option>
            </select>
          </div>

          {/* Sort Direction */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Order</label>
            <select
              value={filters.sortDir || "ASC"}
              onChange={(e) => handleSortDirChange(e.target.value as "ASC" | "DESC")}
              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ASC">Asc</option>
              <option value="DESC">Desc</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveFiltersComponent;
